import React, { useContext, useEffect, useId, useRef, useCallback } from "react";
import { useTheme } from "../lib/ThemeContext";

// Safe hook that returns null if not in ThemeProvider (for edge cases)
function useThemeSafe() {
  try {
    return useTheme();
  } catch {
    return null;
  }
}

export function Button(
  props: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "primary" | "secondary" | "danger" | "outline";
    size?: "sm" | "md" | "lg";
  }
) {
  const { className, variant = "primary", size = "md", ...rest } = props;
  const themeContext = useThemeSafe();
  const needsButtonRings = themeContext?.needsButtonRings ?? false;

  const base =
    "inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--forest-green-glow))] focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:opacity-60 disabled:cursor-not-allowed";

  // Add subtle ring for dark themes that need better button definition
  const ringClass = needsButtonRings && variant === "primary" ? "ring-1 ring-white/10" : "";

  const variants: Record<string, string> = {
    primary: "bg-accent text-accent-foreground shadow-soft hover:shadow-softLg hover:bg-accent/90 hover:-translate-y-0.5 active:shadow-soft active:translate-y-0",
    secondary: "bg-surface text-foreground ring-1 ring-border hover:bg-surface2 hover:-translate-y-0.5 hover:shadow-soft active:bg-surface active:translate-y-0",
    danger: "bg-danger text-white shadow-soft hover:bg-danger/90 hover:shadow-softLg hover:-translate-y-0.5 active:shadow-soft active:translate-y-0",
    outline: "bg-transparent text-foreground ring-1 ring-border hover:bg-surface hover:-translate-y-0.5 active:bg-surface2 active:translate-y-0"
  };
  const sizes: Record<string, string> = {
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-2 text-sm",
    lg: "px-4 py-2.5 text-base"
  };
  return <button className={[base, variants[variant], sizes[size], ringClass, className].filter(Boolean).join(" ")} {...rest} />;
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement> & { label?: string; hint?: string }) {
  const { className, label, hint, id, ...rest } = props;
  const inputId = id ?? (label ? `input-${label.replace(/\s+/g, "-").toLowerCase()}` : undefined);

  return (
    <label className="block">
      {label ? <div className="mb-1 text-sm font-medium text-foreground">{label}</div> : null}
      <input
        id={inputId}
        className={[
          "w-full rounded-md bg-surface px-3 py-2 text-sm text-foreground shadow-input outline-none placeholder:text-muted focus-visible:ring-2 focus-visible:ring-[hsl(var(--forest-green-glow))] focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-60 transition-shadow duration-200",
          className
        ]
          .filter(Boolean)
          .join(" ")}
        {...rest}
      />
      {hint ? <div className="mt-1 text-xs text-muted">{hint}</div> : null}
    </label>
  );
}

export function Select(
  props: React.SelectHTMLAttributes<HTMLSelectElement> & { label?: string; hint?: string; children: React.ReactNode }
) {
  const { className, label, hint, children, ...rest } = props;
  return (
    <label className="block">
      {label ? <div className="mb-1 text-sm font-medium text-foreground">{label}</div> : null}
      <select
        className={[
          "w-full rounded-md bg-surface px-3 py-2 text-sm text-foreground shadow-input outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--forest-green-glow))] focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-60 transition-shadow duration-200",
          className
        ]
          .filter(Boolean)
          .join(" ")}
        {...rest}
      >
        {children}
      </select>
      {hint ? <div className="mt-1 text-xs text-muted">{hint}</div> : null}
    </label>
  );
}

export function Card(props: {
  title?: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  compact?: boolean;
  interactive?: boolean;
}) {
  const { compact = false, interactive = false } = props;
  const themeContext = useThemeSafe();
  const needsGlassEffect = themeContext?.needsGlassEffect ?? false;

  const padding = compact ? "p-3" : "p-5";
  const titleMargin = compact ? "mb-2" : "mb-3";
  const hoverClass = interactive || props.onClick
    ? "hover:-translate-y-1 hover:shadow-softLg cursor-pointer"
    : "";

  // Glass effect for Pro theme
  const glassClass = needsGlassEffect
    ? "backdrop-blur-xl bg-surface/80 border border-border/30"
    : "bg-surface";

  return (
    <div
      className={[
        `rounded-lg ${glassClass} ${padding} shadow-soft transition-all duration-300 ease-out`,
        hoverClass,
        props.className
      ].filter(Boolean).join(" ")}
      onClick={props.onClick}
    >
      {props.title ? <div className={`${titleMargin} text-sm font-semibold text-foreground`}>{props.title}</div> : null}
      {props.children}
    </div>
  );
}

export function Alert(props: { title?: string; items: string[]; variant?: "error" | "info" }) {
  if (!props.items.length) return null;
  const variant = props.variant ?? "error";
  const variantClasses = {
    error: "border-danger/20 bg-danger-bg text-danger-foreground",
    info: "border-accent/20 bg-accent/10 text-foreground",
  };
  return (
    <div role={variant === "error" ? "alert" : "status"} className={`rounded-md border p-3 text-sm ${variantClasses[variant]}`}>
      {props.title ? <div className="font-semibold">{props.title}</div> : null}
      <ul className="mt-1 list-disc pl-5">
        {props.items.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    </div>
  );
}

export function Spinner(props: { label?: string; className?: string }) {
  return (
    <div className={["flex items-center gap-2 text-sm text-muted", props.className].filter(Boolean).join(" ")}>
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-accent" />
      {props.label ? <div>{props.label}</div> : null}
    </div>
  );
}

export function PageHeader(props: { title: string; subtitle?: string; children?: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-semibold font-display text-foreground">{props.title}</h1>
      {props.subtitle && <p className="mt-1 text-sm text-muted">{props.subtitle}</p>}
      {props.children}
    </div>
  );
}

export function Badge(props: {
  children: React.ReactNode;
  variant?: "default" | "success" | "warning" | "danger" | "secondary";
  size?: "sm" | "md";
  className?: string;
}) {
  const { variant = "default", size = "md", className } = props;
  const variants: Record<string, string> = {
    default: "bg-[hsl(var(--forest-green-glow))] text-accent",
    success: "bg-[hsl(var(--forest-green-glow))] text-[hsl(var(--forest-green))]",
    warning: "bg-[hsl(var(--terracotta-glow))] text-[hsl(var(--terracotta))]",
    danger: "bg-danger-bg text-danger-foreground",
    secondary: "bg-surface2 text-muted"
  };
  const sizes: Record<string, string> = {
    sm: "px-1.5 py-0.5 text-xs",
    md: "px-2 py-1 text-xs"
  };
  return (
    <span className={["inline-flex items-center rounded-full font-medium", variants[variant], sizes[size], className].filter(Boolean).join(" ")}>
      {props.children}
    </span>
  );
}

export function Modal(props: {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  const themeContext = useThemeSafe();
  const needsGlassEffect = themeContext?.needsGlassEffect ?? false;
  const titleId = useId();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Store the element that triggered the modal
  useEffect(() => {
    if (props.isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    }
  }, [props.isOpen]);

  // Focus trap and ESC key handler
  useEffect(() => {
    if (!props.isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        props.onClose();
        return;
      }

      if (e.key === "Tab" && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [props.isOpen, props.onClose]);

  // Auto-focus the modal when it opens; restore focus on close
  useEffect(() => {
    if (props.isOpen && modalRef.current) {
      modalRef.current.focus();
    }

    return () => {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === "function") {
        previousFocusRef.current.focus();
      }
    };
  }, [props.isOpen]);

  if (!props.isOpen) return null;

  const modalBg = needsGlassEffect
    ? "backdrop-blur-xl bg-surface/90 border border-border/40"
    : "bg-surface";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-overlay/50" onClick={props.onClose} />
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={props.title ? titleId : undefined}
        tabIndex={-1}
        className={`relative z-10 w-full max-w-md rounded-lg ${modalBg} p-6 shadow-softLg outline-none`}
      >
        {props.title && (
          <h2 id={titleId} className="mb-4 text-lg font-semibold text-foreground">{props.title}</h2>
        )}
        {props.children}
      </div>
    </div>
  );
}

export function EmptyState(props: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}) {
  const Icon = props.icon;
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {Icon && <Icon className="h-12 w-12 text-muted mb-4" />}
      <h3 className="text-lg font-medium text-foreground">{props.title}</h3>
      {props.description && <p className="mt-1 text-sm text-muted">{props.description}</p>}
      {props.action && (
        <Button className="mt-4" onClick={props.action.onClick}>
          {props.action.label}
        </Button>
      )}
    </div>
  );
}

export function Tooltip(props: {
  text: string;
  position?: "top" | "bottom";
  className?: string;
  children: React.ReactNode;
}) {
  const { position = "top", className } = props;
  const id = useId();
  return (
    <span className={["group/tooltip relative inline-flex", className].filter(Boolean).join(" ")} aria-describedby={id}>
      {props.children}
      <span
        id={id}
        role="tooltip"
        className={[
          "pointer-events-none absolute left-1/2 -translate-x-1/2 z-50",
          "opacity-0 group-hover/tooltip:opacity-100 transition-opacity delay-150",
          "rounded-md bg-foreground text-bg px-2 py-1 text-xs max-w-[200px] w-max text-center whitespace-normal",
          position === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
        ].join(" ")}
      >
        {props.text}
        <span
          className={[
            "absolute left-1/2 -translate-x-1/2 border-4 border-transparent",
            position === "top"
              ? "top-full border-t-foreground"
              : "bottom-full border-b-foreground",
          ].join(" ")}
        />
      </span>
    </span>
  );
}
